import type { ApplicationProposal, CloudNodeType } from '@ai-secure-twin/shared';

/**
 * The shadow-surface radar (ADR 0008, UI direction C): every proposal discovery made, placed by
 * what lies behind it. The centre is the crown jewels — the nearer a dot, the more its hosts
 * lead to. Rings: secrets or data, then compute, then the edge alone. Pure and deterministic:
 * a proposal sits at the same angle on every render, so people learn where things are.
 */

export type RadarReach = 'DATA' | 'COMPUTE' | 'EDGE';
export type RadarTone = 'danger' | 'warn' | 'owned';

export interface RadarDot {
  key: string;
  name: string;
  x: number;
  y: number;
  r: number;
  reach: RadarReach;
  tone: RadarTone;
  /** What the dot stands for, in words, for the dot's label and screen readers. */
  why: string;
}

export const RADAR_SIZE = 340;
const CENTRE = RADAR_SIZE / 2;
/** Inner and outer radius of each ring. */
export const RINGS: Record<RadarReach, [number, number]> = {
  DATA: [26, 66],
  COMPUTE: [80, 118],
  EDGE: [132, 160],
};

const DATA_TYPES: ReadonlySet<CloudNodeType> = new Set([
  'SECRET',
  'RDS_DATABASE',
  'DYNAMODB_TABLE',
  'ELASTICACHE_CLUSTER',
  'S3_BUCKET',
]);
const COMPUTE_TYPES: ReadonlySet<CloudNodeType> = new Set([
  'ECS_SERVICE',
  'LAMBDA_FUNCTION',
  'STATE_MACHINE',
]);

export function reachOf(p: ApplicationProposal): RadarReach {
  if (p.resources.some((r) => DATA_TYPES.has(r.type))) return 'DATA';
  if (p.resources.some((r) => COMPUTE_TYPES.has(r.type))) return 'COMPUTE';
  return 'EDGE';
}

/** A secret behind it, or data the internet can read directly, is danger; anything else unowned is a warning. */
export function toneOf(p: ApplicationProposal): RadarTone {
  if (p.status === 'COVERED') return 'owned';
  const exposed = p.resources.some(
    (r) => r.type === 'SECRET' || (r.type === 'S3_BUCKET' && r.public),
  );
  return exposed ? 'danger' : 'warn';
}

function whyOf(p: ApplicationProposal, reach: RadarReach, tone: RadarTone): string {
  if (tone === 'owned') return 'owned by an application';
  const lead =
    p.resources.some((r) => r.type === 'S3_BUCKET' && r.public) && reach === 'DATA'
      ? 'public data'
      : p.resources.some((r) => r.type === 'SECRET')
        ? 'leads to a secret'
        : reach === 'DATA'
          ? 'leads to data'
          : reach === 'COMPUTE'
            ? 'leads to compute'
            : 'edge only';
  return `${lead}, no owner`;
}

/** A small stable number from a string, so a ring's dots do not all start at twelve o'clock. */
function seed(text: string): number {
  let h = 2166136261;
  for (let i = 0; i < text.length; i += 1) h = Math.imul(h ^ text.charCodeAt(i), 16777619);
  return (h >>> 0) / 2 ** 32;
}

export function layoutRadar(proposals: readonly ApplicationProposal[]): RadarDot[] {
  const visible = proposals
    .filter((p) => p.status !== 'DISMISSED')
    .slice()
    .sort((a, b) => (a.key < b.key ? -1 : a.key > b.key ? 1 : 0));
  const byRing = new Map<RadarReach, ApplicationProposal[]>();
  for (const p of visible) {
    const reach = reachOf(p);
    byRing.set(reach, [...(byRing.get(reach) ?? []), p]);
  }
  const dots: RadarDot[] = [];
  for (const [reach, ring] of byRing) {
    const [inner, outer] = RINGS[reach];
    const offset = seed(reach) * Math.PI * 2;
    ring.forEach((p, i) => {
      const angle = offset + (i / ring.length) * Math.PI * 2;
      // Alternate between two radii in the band so neighbours do not collide.
      const radius = ring.length > 6 && i % 2 === 1 ? outer - 6 : (inner + outer) / 2;
      const tone = toneOf(p);
      dots.push({
        key: p.key,
        name: p.name,
        x: Math.round((CENTRE + Math.cos(angle) * radius) * 10) / 10,
        y: Math.round((CENTRE + Math.sin(angle) * radius) * 10) / 10,
        r: Math.min(9, 4 + p.hosts.length),
        reach,
        tone,
        why: whyOf(p, reach, tone),
      });
    });
  }
  // Danger first in reading order, then by key: the order a person tabs through them.
  const rank: Record<RadarTone, number> = { danger: 0, warn: 1, owned: 2 };
  return dots.sort((a, b) => rank[a.tone] - rank[b.tone] || (a.key < b.key ? -1 : 1));
}
